import React, { useEffect } from "react";
import { api } from "./api";
import { useAuth } from "./auth-context";

const DEFAULT_BRAND_COLOR = "#EF694C";
const DEFAULT_BRAND_RGB = "239 105 76";

/**
 * Convert a CSS color value (#RGB, #RRGGBB, rgb(...), or "R G B" / "R, G, B")
 * into a space-separated "R G B" triplet usable inside Tailwind utilities via
 *   rgb(var(--brand-rgb) / <alpha-value>)
 * Returns null on parse failure so the caller can fall back to the default.
 */
export function hexToRgbTriplet(input) {
  if (!input || typeof input !== "string") return null;
  const v = input.trim();

  // #RGB / #RRGGBB
  if (v.startsWith("#")) {
    let hex = v.slice(1);
    if (hex.length === 3) {
      hex = hex
        .split("")
        .map((c) => c + c)
        .join("");
    }
    if (hex.length !== 6 || /[^0-9a-fA-F]/.test(hex)) return null;
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    return `${r} ${g} ${b}`;
  }

  // rgb(r, g, b) / rgba(r, g, b, a)
  const m = v.match(/rgba?\(\s*(\d+)\s*[, ]\s*(\d+)\s*[, ]\s*(\d+)/i);
  if (m) return `${m[1]} ${m[2]} ${m[3]}`;

  // Already an "R G B" triplet
  const t = v.match(/^(\d{1,3})\s+(\d{1,3})\s+(\d{1,3})$/);
  if (t) return `${t[1]} ${t[2]} ${t[3]}`;

  return null;
}

/**
 * Write both --brand (display value) and --brand-rgb (triplet for alpha-aware
 * utilities) onto :root. Centralized here so every entry point stays in sync.
 */
export function applyBrandColor(color) {
  const display = color || DEFAULT_BRAND_COLOR;
  const triplet = hexToRgbTriplet(display) || DEFAULT_BRAND_RGB;
  const root = document.documentElement;
  root.style.setProperty("--brand", display);
  root.style.setProperty("--brand-rgb", triplet);
}

/**
 * Applies the per-tenant branding color to --brand / --brand-rgb at runtime.
 * Falls back to the FyreCRM brand orange when no tenant or no value.
 */
export function BrandColorProvider({ children }) {
  const { tenant } = useAuth();

  useEffect(() => {
    let cancelled = false;

    async function loadBranding() {
      if (!tenant) {
        applyBrandColor(DEFAULT_BRAND_COLOR);
        return;
      }
      try {
        const r = await api.get("/api/settings/tenant");
        if (!cancelled) {
          applyBrandColor(r.data?.branding_color || DEFAULT_BRAND_COLOR);
        }
      } catch {
        if (!cancelled) applyBrandColor(DEFAULT_BRAND_COLOR);
      }
    }
    loadBranding();
    return () => {
      cancelled = true;
    };
  }, [tenant]);

  return children;
}
